import { IonPage, IonToast } from '@ionic/react';
import { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_CONFIG from '../config/api';
import AuthNavigation from '../components/AuthNavigation';
import '../styles/auth.css';
import { getOrganizationFromUrl, formatOrganizationName } from '../helpers/registrationLinkUtils';
import LocationSelector from '../components/LocationSelector';

const MultiVendorRegistration = () => {
    const history = useHistory();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const redirectPath = queryParams.get('redirect');

    // Auto-redirect if already logged in
    useEffect(() => {
        const userId = sessionStorage.getItem('user_id');
        if (userId && redirectPath) {
            history.replace(redirectPath);
        }
    }, [history, redirectPath]);

    const [registrationType, setRegistrationType] = useState('individual'); // 'individual' or 'organization'
    const [currentStep, setCurrentStep] = useState(1);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [organizationTypes, setOrganizationTypes] = useState([]);
    const [organizationFromUrl, setOrganizationFromUrl] = useState(null);
    const [isOrganizationRegistration, setIsOrganizationRegistration] = useState(false);
    const [showIndividualPassword, setShowIndividualPassword] = useState(false);
    const [showIndividualConfirmPassword, setShowIndividualConfirmPassword] = useState(false);
    const [showOrgPassword, setShowOrgPassword] = useState(false);
    const [showOrgConfirmPassword, setShowOrgConfirmPassword] = useState(false);
    const [registrationEnabled, setRegistrationEnabled] = useState(true);

    // Individual registration form data
    const [individualData, setIndividualData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
        country: '',
        state: '',
        localGovernment: '',
        registrationCode: '' // New field for manual Org ID entry
    });

    // Organization registration form data
    const [organizationData, setOrganizationData] = useState({
        organizationName: '',
        organizationType: '',
        description: '',
        adminName: '',
        adminEmail: '',
        adminPassword: '',
        confirmPassword: '',
        contactEmail: '',
        phone: '',
        website: '',
        address: '',
        city: '',
        state: '',
        country: ''
    });


    useEffect(() => {
        // Check if user is registering through an organization link
        const orgInfo = getOrganizationFromUrl();
        if (orgInfo) {
            setOrganizationFromUrl(orgInfo);
            setIsOrganizationRegistration(true);
            setRegistrationType('individual'); // Force individual registration for organization members
            setCurrentStep(2); // Skip the registration type selector
        }

        // Load organization types
        fetchOrganizationTypes();

        // Check if registration is enabled
        const checkRegistrationSettings = async () => {
            try {
                const response = await axios.get(API_CONFIG.MANAGE_SETTINGS);
                if (response.data.success) {
                    setRegistrationEnabled(response.data.settings.registration_enabled !== false);
                }
            } catch (e) {
                console.error("Failed to fetch settings", e);
            }
        };
        checkRegistrationSettings();
    }, []);

    const fetchOrganizationTypes = async () => {
        try {
            console.log('Fetching organization types from backend...');
            const response = await axios.get(API_CONFIG.GET_ORGANIZATION_TYPES);
            console.log('Organization types response:', response.data);
            if (response.data.success) {
                setOrganizationTypes(response.data.organizationTypes);
                console.log('Organization types loaded successfully:', response.data.organizationTypes);
            } else {
                console.log('Backend returned no organization types, using fallback');
                setFallbackOrganizationTypes();
            }
        } catch (error) {
            console.error('Failed to fetch organization types:', error);
            console.log('Using fallback organization types');
            setFallbackOrganizationTypes();
        }
    };

    const setFallbackOrganizationTypes = () => {
        const fallbackTypes = [
            { id: 1, name: 'School', description: 'Educational institutions', icon: 'school' },
            { id: 2, name: 'Government', description: 'Government agencies', icon: 'government' },
            { id: 3, name: 'Company', description: 'Private companies', icon: 'business' },
            { id: 4, name: 'Church', description: 'Religious organizations', icon: 'church' },
            { id: 5, name: 'NGO', description: 'Non-governmental organizations', icon: 'heart' },
            { id: 6, name: 'Association', description: 'Professional associations', icon: 'people' },
            { id: 7, name: 'Other', description: 'Other organizations', icon: 'globe' }
        ];
        setOrganizationTypes(fallbackTypes);
        console.log('Fallback organization types set:', fallbackTypes);
    };

    const handleIndividualInputChange = (field, value) => {
        setIndividualData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleOrganizationInputChange = (field, value) => {
        setOrganizationData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Using LocationSelector component handles state logic now

    const validateStep = (step) => {
        // Step 1 is registration type selector - just check if type is selected
        if (step === 1 && !isOrganizationRegistration) {
            return registrationType && (registrationType === 'individual' || registrationType === 'organization');
        }

        if (registrationType === 'individual') {
            if (isOrganizationRegistration) {
                // For organization member registration, steps are shifted
                switch (step) {
                    case 2: // Personal Information (first step for organization members)
                        return individualData.name && individualData.email && individualData.password && individualData.confirmPassword;
                    case 3: // Location Information (second step for organization members)
                        return individualData.country && individualData.state && individualData.localGovernment;
                    default:
                        return true;
                }
            } else {
                // Regular individual registration
                switch (step) {
                    case 2: // Individual Step 1 - Personal Information
                        return individualData.name && individualData.email && individualData.password && individualData.confirmPassword;
                    case 3: // Individual Step 2 - Location Information
                        return individualData.country && individualData.state && individualData.localGovernment;
                    default:
                        return true;
                }
            }
        } else {
            // Organization registration
            switch (step) {
                case 2: // Organization Step 1 - Organization Details
                    return organizationData.organizationName && organizationData.organizationType && organizationData.description;
                case 3: // Organization Step 2 - Administrator Account
                    return organizationData.adminName && organizationData.adminEmail && organizationData.adminPassword && organizationData.confirmPassword;
                case 4: // Organization Step 3 - Contact & Location
                    return organizationData.contactEmail && organizationData.country;
                default:
                    return true;
            }
        }
    };

    const nextStep = () => {
        console.log('Next step clicked. Current step:', currentStep);
        console.log('Registration type:', registrationType);
        console.log('Validation result:', validateStep(currentStep));

        if (validateStep(currentStep)) {
            const password = registrationType === 'individual' ? individualData.password : organizationData.adminPassword;
            const confirmPassword = registrationType === 'individual' ? individualData.confirmPassword : organizationData.confirmPassword;

            if (password !== confirmPassword) {
                setError('Passwords do not match');
                return;
            }
            setError('');
            setCurrentStep(currentStep + 1);
        } else {
            console.log('Validation failed for step:', currentStep);
            if (registrationType === 'individual') {
                console.log('Individual data:', individualData);
            } else {
                console.log('Organization data:', organizationData);
            }
            setError('Please fill in all required fields');
        }
    };

    const prevStep = () => {
        setError('');
        setCurrentStep(currentStep - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            let submitData;

            if (registrationType === 'individual') {
                if (individualData.password !== individualData.confirmPassword) {
                    setError('Passwords do not match');
                    setIsLoading(false);
                    return;
                }

                submitData = {
                    registrationType: 'individual',
                    ...individualData,
                    // Add organization info if registering through organization link
                    ...(isOrganizationRegistration && organizationFromUrl && {
                        targetOrganizationId: organizationFromUrl.organizationId,
                        registrationCode: organizationFromUrl.registrationCode
                    })
                };
            } else {
                if (organizationData.adminPassword !== organizationData.confirmPassword) {
                    setError('Passwords do not match');
                    setIsLoading(false);
                    return;
                }

                submitData = {
                    registrationType: 'organization',
                    ...organizationData
                };
            }

            const response = await axios.post(API_CONFIG.REGISTER_MULTIVENDOR, submitData, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (response.data.success) {
                setSuccess(response.data.message || 'Registration successful!');
                setShowToast(true);

                if (registrationType === 'organization') {
                    // For organization registration, show pending approval message
                    if (response.data.pending_approval) {
                        setSuccess('Registration submitted! Your organization is pending approval. You will be notified once approved.');
                        setShowToast(true);
                        // Redirect to login page after showing message
                        setTimeout(() => {
                            history.push('/page/login?m=org_pending');
                        }, 3000);
                    } else {
                        // If somehow already approved (shouldn't happen normally)
                        sessionStorage.setItem('user_id', response.data.user_id);
                        sessionStorage.setItem('organization_id', response.data.organization_id);
                        setTimeout(() => {
                            window.location.href = '/admin';
                        }, 2000);
                    }
                } else {
                    // For individual registration, redirect to login with organization context
                    const loginRedirect = isOrganizationRegistration
                        ? `/page/login?m=registration_success&org=${organizationFromUrl?.organizationName || ''}`
                        : '/page/login?m=registration_success';

                    const finalLoginPath = redirectPath
                        ? `${loginRedirect}&redirect=${encodeURIComponent(redirectPath)}`
                        : loginRedirect;

                    setTimeout(() => {
                        history.push(finalLoginPath);
                    }, 2000);
                }
            } else {
                setError(response.data.error || 'Registration failed');
            }
        } catch (err) {
            setError('Registration failed. Please try again.');
            console.error('Registration error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    if (!registrationEnabled) {
        return (
            <IonPage className="flex items-center justify-center bg-gray-50 p-6">
                <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-10 text-center border border-gray-100">
                    <div className="bg-orange-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-10 h-10 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h2 className="text-3xl font-extrabold text-gray-900 mb-4 tracking-tight">Registration Paused</h2>
                    <p className="text-gray-500 mb-8 leading-relaxed">
                        Public registration is currently disabled by the system administrator.
                        Please try again later or contact support if you need immediate assistance.
                    </p>
                    <div className="space-y-4">
                        <Link
                            to="/page/login"
                            className="block w-full py-4 px-6 bg-slate-900 text-white font-bold rounded-2xl hover:bg-slate-800 transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-slate-200"
                        >
                            Return to Login
                        </Link>
                    </div>
                </div>
            </IonPage>
        );
    }

    const renderRegistrationTypeSelector = () => (
        <div className="space-y-8">
            {/* Header Section */}
            <div className="text-center">
                <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 bg-clip-text text-transparent mb-3">
                    Choose Registration Type
                </h2>
                <p className="text-gray-500 text-sm md:text-base">Select how you want to use PeopPoll</p>
            </div>

            {/* Registration Type Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div
                    className={`reg-type-card ${registrationType === 'individual' ? 'selected-individual' : ''}`}
                    onClick={() => setRegistrationType('individual')}
                >
                    <div className={`reg-type-icon ${registrationType === 'individual' ? 'individual' : ''}`}>
                        <svg className={`w-10 h-10 ${registrationType === 'individual' ? 'text-orange-500' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                    </div>
                    <h3 className={`text-xl font-bold mb-2 transition-colors duration-300 ${registrationType === 'individual' ? 'text-[#ff9933]' : 'text-slate-700'}`}>Individual User</h3>
                    <p className="text-slate-500 text-sm leading-relaxed text-center px-4">
                        Join as an individual to participate in polls and surveys
                    </p>
                    {registrationType === 'individual' && (
                        <div className="mt-6 flex items-center justify-center animate-bounce-in">
                            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold bg-[#ff9933] text-white shadow-lg shadow-orange-500/30">
                                <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Selected
                            </span>
                        </div>
                    )}
                </div>

                {/* Organization Card */}
                <div
                    className={`reg-type-card ${registrationType === 'organization' ? 'selected-organization' : ''}`}
                    onClick={() => setRegistrationType('organization')}
                >
                    <div className={`reg-type-icon ${registrationType === 'organization' ? 'organization' : ''}`}>
                        <svg className={`w-10 h-10 ${registrationType === 'organization' ? 'text-green-600' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                    </div>
                    <h3 className={`text-xl font-bold mb-2 transition-colors duration-300 ${registrationType === 'organization' ? 'text-green-600' : 'text-slate-700'}`}>Organization</h3>
                    <p className="text-slate-500 text-sm leading-relaxed text-center px-4">
                        Create an organization to manage members and conduct polls
                    </p>
                    {registrationType === 'organization' && (
                        <div className="mt-6 flex items-center justify-center animate-bounce-in">
                            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold bg-green-600 text-white shadow-lg shadow-green-500/30">
                                <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Selected
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Continue Button */}
            <div className="text-center pt-4">
                <button
                    onClick={() => setCurrentStep(2)}
                    disabled={!registrationType}
                    className="btn-primary inline-flex items-center justify-center px-10 py-3.5 text-base font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <span>Continue</span>
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                </button>
            </div>
        </div>
    );

    const renderIndividualStep1 = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Personal Information</h2>
                <p className="text-gray-600">Tell us about yourself</p>
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Full Name *
                </label>
                <input
                    type="text"
                    value={individualData.name}
                    onChange={(e) => handleIndividualInputChange('name', e.target.value)}
                    placeholder="Enter your full name"
                    className="form-input"
                    required
                />
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                    Email Address *
                </label>
                <input
                    type="email"
                    value={individualData.email}
                    onChange={(e) => handleIndividualInputChange('email', e.target.value)}
                    placeholder="Enter your email address"
                    className="form-input"
                    required
                />
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Phone Number
                </label>
                <input
                    type="tel"
                    value={individualData.phone}
                    onChange={(e) => handleIndividualInputChange('phone', e.target.value)}
                    placeholder="Enter your phone number"
                    className="form-input"
                />
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Password *
                </label>
                <div className="relative">
                    <input
                        type={showIndividualPassword ? "text" : "password"}
                        value={individualData.password}
                        onChange={(e) => handleIndividualInputChange('password', e.target.value)}
                        placeholder="Create a secure password"
                        className="form-input pr-10"
                        required
                    />
                    <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowIndividualPassword(!showIndividualPassword)}
                    >
                        {showIndividualPassword ? (
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                            </svg>
                        ) : (
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Confirm Password *
                </label>
                <div className="relative">
                    <input
                        type={showIndividualConfirmPassword ? "text" : "password"}
                        value={individualData.confirmPassword}
                        onChange={(e) => handleIndividualInputChange('confirmPassword', e.target.value)}
                        placeholder="Confirm your password"
                        className="form-input pr-10"
                        required
                    />
                    <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowIndividualConfirmPassword(!showIndividualConfirmPassword)}
                    >
                        {showIndividualConfirmPassword ? (
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                            </svg>
                        ) : (
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Organization ID (Optional)
                </label>
                <input
                    type="text"
                    value={individualData.registrationCode}
                    onChange={(e) => handleIndividualInputChange('registrationCode', e.target.value)}
                    placeholder="Enter Organization ID (e.g. REG-XXXX)"
                    className="form-input"
                />
                <p className="text-xs text-gray-400 mt-1">If you were given an ID by your organization, enter it here to join them.</p>
            </div>
        </div>
    );

    const renderIndividualStep2 = () => (
        <div className="space-y-6">


            <LocationSelector 
                country={individualData.country}
                state={individualData.state}
                localGovernment={individualData.localGovernment}
                onChange={handleIndividualInputChange}
                required={true}
            />
        </div>
    );

    const renderOrganizationStep1 = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Organization Details</h2>
                <p className="text-gray-600">Tell us about your organization</p>
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Organization Name *
                </label>
                <input
                    type="text"
                    value={organizationData.organizationName}
                    onChange={(e) => handleOrganizationInputChange('organizationName', e.target.value)}
                    placeholder="Enter organization name"
                    className="form-input"
                    required
                />
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a1.994 1.994 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Organization Type *
                </label>
                <select
                    value={organizationData.organizationType}
                    onChange={(e) => handleOrganizationInputChange('organizationType', e.target.value)}
                    className="form-input"
                    required
                >
                    <option value="">Select Organization Type</option>
                    {organizationTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                            {type.name} - {type.description}
                        </option>
                    ))}
                </select>
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Description *
                </label>
                <textarea
                    value={organizationData.description}
                    onChange={(e) => handleOrganizationInputChange('description', e.target.value)}
                    placeholder="Describe your organization's purpose and activities"
                    className="form-input min-h-[100px]"
                    required
                />
            </div>
        </div>
    );

    const renderOrganizationStep2 = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Administrator Account</h2>
                <p className="text-gray-600">Create the main admin account for your organization</p>
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Admin Full Name *
                </label>
                <input
                    type="text"
                    value={organizationData.adminName}
                    onChange={(e) => handleOrganizationInputChange('adminName', e.target.value)}
                    placeholder="Enter admin's full name"
                    className="form-input"
                    required
                />
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                    Admin Email *
                </label>
                <input
                    type="email"
                    value={organizationData.adminEmail}
                    onChange={(e) => handleOrganizationInputChange('adminEmail', e.target.value)}
                    placeholder="Enter admin's email address"
                    className="form-input"
                    required
                />
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Admin Password *
                </label>
                <div className="relative">
                    <input
                        type={showOrgPassword ? "text" : "password"}
                        value={organizationData.adminPassword}
                        onChange={(e) => handleOrganizationInputChange('adminPassword', e.target.value)}
                        placeholder="Create a secure password"
                        className="form-input pr-10"
                        required
                    />
                    <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowOrgPassword(!showOrgPassword)}
                    >
                        {showOrgPassword ? (
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                            </svg>
                        ) : (
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Confirm Password *
                </label>
                <div className="relative">
                    <input
                        type={showOrgConfirmPassword ? "text" : "password"}
                        value={organizationData.confirmPassword}
                        onChange={(e) => handleOrganizationInputChange('confirmPassword', e.target.value)}
                        placeholder="Confirm the password"
                        className="form-input pr-10"
                        required
                    />
                    <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowOrgConfirmPassword(!showOrgConfirmPassword)}
                    >
                        {showOrgConfirmPassword ? (
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                            </svg>
                        ) : (
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );

    const renderOrganizationStep3 = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Contact & Location</h2>
                <p className="text-gray-600">Organization contact information</p>
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                    Contact Email *
                </label>
                <input
                    type="email"
                    value={organizationData.contactEmail}
                    onChange={(e) => handleOrganizationInputChange('contactEmail', e.target.value)}
                    placeholder="Organization contact email"
                    className="form-input"
                    required
                />
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Phone Number
                </label>
                <input
                    type="tel"
                    value={organizationData.phone}
                    onChange={(e) => handleOrganizationInputChange('phone', e.target.value)}
                    placeholder="Organization phone number"
                    className="form-input"
                />
            </div>

            <LocationSelector 
                country={organizationData.country}
                state={organizationData.state}
                localGovernment={organizationData.city}
                onChange={(field, value) => handleOrganizationInputChange(field === 'localGovernment' ? 'city' : field, value)}
                required={true}
            />

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Address
                </label>
                <input
                    type="text"
                    value={organizationData.address}
                    onChange={(e) => handleOrganizationInputChange('address', e.target.value)}
                    placeholder="Organization address"
                    className="form-input"
                />
            </div>

            <div className="form-group">
                <label className="form-label">
                    <svg className="inline w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Website
                </label>
                <input
                    type="url"
                    value={organizationData.website}
                    onChange={(e) => handleOrganizationInputChange('website', e.target.value)}
                    placeholder="Organization website (optional)"
                    className="form-input"
                />
            </div>
        </div>
    );

    const getMaxSteps = () => {
        if (isOrganizationRegistration) {
            return 3; // Individual registration for organization members
        }
        return registrationType === 'individual' ? 3 : 4;
    };

    const renderStepContent = () => {
        // Skip registration type selector if registering through organization link
        if (currentStep === 1 && !isOrganizationRegistration) {
            return renderRegistrationTypeSelector();
        }

        if (registrationType === 'individual') {
            if (isOrganizationRegistration) {
                // For organization member registration, start directly with personal info
                switch (currentStep) {
                    case 2: return renderIndividualStep1(); // Personal Information
                    case 3: return renderIndividualStep2(); // Location Information
                    default: return null;
                }
            } else {
                // Regular individual registration flow
                switch (currentStep) {
                    case 2: return renderIndividualStep1(); // Personal Information
                    case 3: return renderIndividualStep2(); // Location Information
                    default: return null;
                }
            }
        } else {
            // Organization registration flow
            switch (currentStep) {
                case 2: return renderOrganizationStep1();
                case 3: return renderOrganizationStep2();
                case 4: return renderOrganizationStep3();
                default: return null;
            }
        }
    };

    const renderNavigation = () => {
        const maxSteps = getMaxSteps();
        const canProceed = validateStep(currentStep);

        return (
            <div className="flex justify-between items-center mt-8 pt-6 border-t">
                <button
                    type="button"
                    onClick={prevStep}
                    className={`btn-secondary px-6 py-2 ${(currentStep <= 1 && !isOrganizationRegistration) ||
                        (currentStep <= 2 && isOrganizationRegistration)
                        ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    disabled={
                        (currentStep <= 1 && !isOrganizationRegistration) ||
                        (currentStep <= 2 && isOrganizationRegistration)
                    }
                >
                    Previous
                </button>

                <div className="flex space-x-2">
                    {Array.from({ length: maxSteps }, (_, i) => (
                        <div
                            key={i}
                            className={`w-3 h-3 rounded-full ${i + 1 < currentStep ? 'bg-green-500' :
                                i + 1 === currentStep ? 'bg-blue-500' : 'bg-gray-300'
                                }`}
                        />
                    ))}
                </div>

                {currentStep < maxSteps ? (
                    <button
                        type="button"
                        onClick={nextStep}
                        className={`btn-primary px-6 py-2 ${!canProceed ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={!canProceed}
                    >
                        Next
                    </button>
                ) : (
                    <button
                        type="submit"
                        className={`btn-primary px-6 py-2 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Creating Account...' : 'Create Account'}
                    </button>
                )}
            </div>
        );
    };

    return (
        <IonPage className="auth-page">
            <div className="auth-container">
                {/* Floating background elements */}
                <div className="floating-element"></div>
                <div className="floating-element-2"></div>

                {/* Custom Header integrated into background */}
                <div className="auth-header">
                    <button onClick={() => history.goBack()} className="back-button">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span>Back</span>
                    </button>
                    <h2 className="auth-title-header">Register</h2>
                    <div className="w-16"></div> {/* Spacer for centering */}
                </div>

                <div className="auth-content">
                    <div className="auth-card registration-card animate-fade-in-up">
                        {/* Logo */}
                        <div className="text-center mb-8">
                            <img
                                className="auth-logo animate-bounce-in"
                                src="/assets/final_peoppoll_logo.png"
                                alt="PeoPoll Logo"
                            />
                            {isOrganizationRegistration && organizationFromUrl ? (
                                <div>
                                    <h1 className="auth-title">Join {formatOrganizationName(organizationFromUrl.organizationName)}</h1>
                                    <p className="auth-subtitle">Create your account to become a member</p>
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                                        <div className="flex items-center justify-center text-blue-800">
                                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span className="text-sm font-medium">
                                                You're registering as a member of {formatOrganizationName(organizationFromUrl.organizationName)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <h1 className="auth-title">Join PeopPoll</h1>
                                    <p className="auth-subtitle">Create your account to get started</p>
                                </div>
                            )}
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="error-message animate-fade-in-up mb-6">
                                <div className="flex items-center">
                                    <svg className="w-5 h-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    {error}
                                </div>
                            </div>
                        )}

                        {/* Debug Info (only in development) */}
                        {/* {process.env.NODE_ENV === 'development' && (
                            <div className="bg-gray-100 p-4 rounded-lg mb-6 text-sm">
                                <p><strong>Debug Info:</strong></p>
                                <p>Current Step: {currentStep}</p>
                                <p>Registration Type: {registrationType}</p>
                                <p>Organization Types Loaded: {organizationTypes.length}</p>
                                <p>Backend URL: {API_BASE_URL}/</p>
                            </div>
                        )} */}

                        {/* Registration Form */}
                        <form onSubmit={handleSubmit}>
                            {renderStepContent()}
                            {/* Show navigation for all steps except first step when not organization registration,
                                or starting from step 2 for organization registration */}
                            {((currentStep > 1 && !isOrganizationRegistration) ||
                                (currentStep >= 2 && isOrganizationRegistration)) && renderNavigation()}
                        </form>

                        {/* Sign In Link */}
                        <div className="mt-8 text-center">
                            <p className="text-gray-600 text-sm">
                                Already have an account?{' '}
                                <Link to={redirectPath ? `/page/login?redirect=${encodeURIComponent(redirectPath)}` : "/page/login"} className="link-text">
                                    Sign in here
                                </Link>
                            </p>
                        </div>

                        {/* Cross-navigation to other auth pages */}
                        <AuthNavigation />
                    </div>
                </div>
            </div>

            <IonToast
                isOpen={showToast}
                onDidDismiss={() => setShowToast(false)}
                message={success}
                duration={3000}
                color="success"
            />
        </IonPage>
    );
};

export default MultiVendorRegistration;
